import React, { useState, useEffect } from 'react';
import { logger } from '@fiverr-private/obs';
import { LazyComponent } from '@fiverr-private/ui_utils';
import { countEventCall, countEventNotMounted } from '../../events/countEvent';
import { deprecatedLogger } from '../../utils';
import {
    MIGRATION_MODAL_EVENT_NAME,
    SeamlessModalPayloadValue,
    ModalPayload,
    IS_MIGRATION_MANAGER_MOUNTED,
} from './triggers';
import { ModalType } from './types';
import { MigrationModalManagerProps } from './MigrationModalManager.props';
import { useOldModalMigrationEvents } from './useOldModalMigrationEvents';

const REROUTE_MAP: Partial<Record<ModalType, ModalType>> = {
    [ModalType.SeamlessMoneyBack]: ModalType.SeamlessRegistration,
    [ModalType.SeamlessCustomOfferMoneyBack]: ModalType.SeamlessRegistration,
};

export const MigrationModalManager: React.FC<MigrationModalManagerProps> = ({ userEmail, rollouts = {} }) => {
    const [modalType, setModalType] = useState<ModalType | null>(null);
    const [modalPayload, setModalPayload] = useState<SeamlessModalPayloadValue>({
        source: '',
        biEnrichment: { pageElementName: '' },
    });

    const onClose = () => {
        setModalType(null);
    };

    // README: Need for old events "openFibMigration". DON'T REMOVE THIS
    useOldModalMigrationEvents({
        setModalProps: setModalPayload,
        setModalType,
    });

    useEffect(() => {
        const eventHandler = ({ detail }: CustomEvent<ModalPayload>) => {
            const { variant, payload } = detail;

            const resolvedVariant = REROUTE_MAP[variant] ?? variant;

            setModalType(resolvedVariant);

            // Fixed typo in incorrect name "bqEnrichment". Should be "biEnrichment"
            if ((payload as any).bqEnrichment) {
                logger.warn('Incorrect payload with "bqEnrichment"', {
                    details: {
                        message: 'Should be "biEnrichment"',
                        package: '@fiverr-private/pro-migration',
                        source: payload.source,
                        type: variant,
                    },
                });
                payload.biEnrichment = (payload as any).bqEnrichment;
                delete (payload as any).bqEnrichment;
            }

            // README: To avoid confusion in passing `pageName` as `page.element.name` to BQ, we have to use similar `pageElementName`
            if (!payload.biEnrichment.pageElementName && payload.biEnrichment.pageName) {
                payload.biEnrichment.pageElementName = payload.biEnrichment.pageName;
            }

            setModalPayload(payload);

            countEventCall({ type: variant, source: payload.source });
        };

        window.addEventListener(MIGRATION_MODAL_EVENT_NAME, eventHandler as EventListener);
        window[IS_MIGRATION_MANAGER_MOUNTED] = true;

        return () => {
            window.removeEventListener(MIGRATION_MODAL_EVENT_NAME, eventHandler as EventListener);
            window[IS_MIGRATION_MANAGER_MOUNTED] = false;
        };
    }, []);

    const onError = (error) => {
        logger.error(error, { description: `Failed to lazy load MigrationModal type ${modalType}` });
        if (modalType) {
            countEventNotMounted({ type: modalType, source: modalPayload.source });
        }
    };

    const seamlessModalProps = {
        userEmail: userEmail || '',
        rollouts,
        modalType,
        ...modalPayload,
        onClose: () => {
            if ('onClose' in modalPayload && typeof modalPayload.onClose === 'function') {
                modalPayload.onClose(onClose);
            } else {
                onClose();
            }
        },
    };

    let lazyImport: React.ComponentProps<typeof LazyComponent>['lazyImport'] | null = null;

    switch (modalType) {
        case ModalType.HeadlessSeamlessMigration:
            lazyImport = () =>
                import(/* webpackChunkName: 'HeadlessSeamlessMigrationModal' */ '../HeadlessSeamlessMigrationModal');
            break;
        case ModalType.SeamlessCollaboration:
            lazyImport = () =>
                import(/* webpackChunkName: 'SeamlessCollaborationModal' */ '../SeamlessCollaborationModal');
            break;
        case ModalType.SeamlessPayment:
            lazyImport = () =>
                import(/* webpackChunkName: 'SeamlessCollaborationModal' */ '../SeamlessCollaborationModal');
            break;
        case ModalType.SeamlessRegistration:
            lazyImport = () =>
                import(/* webpackChunkName: 'SeamlessProRegistrationModal' */ '../SeamlessProRegistrationModal');
            break;
        case ModalType.SeamlessHourly:
            lazyImport = () => import(/* webpackChunkName: 'SeamlessHourlyModal' */ '../SeamlessHourlyModal');
            break;
        case ModalType.SeamlessLoyalty:
            lazyImport = () => import(/* webpackChunkName: 'SeamlessLoyaltyModal' */ '../SeamlessLoyaltyModal');
            break;
        case ModalType.SeamlessMoneyBack:
            lazyImport = () => import(/* webpackChunkName: 'SeamlessMoneyBackModal' */ '../SeamlessMoneyBackModal');
            break;
        case ModalType.SeamlessCustomOfferMoneyBack:
            lazyImport = () =>
                import(
                    /* webpackChunkName: 'SeamlessMoneyBackCustomOfferModal' */ '../SeamlessMoneyBackCustomOfferModal'
                );
            break;
        case ModalType.SeamlessCustomOfferRewardsProgram:
            lazyImport = () =>
                import(
                    /* webpackChunkName: 'SeamlessRewardsProgramCustomOfferModal' */ '../SeamlessRewardsProgramModal'
                );
            break;
        case ModalType.SeamlessUpgradeToPro:
            lazyImport = () =>
                import(/* webpackChunkName: 'SeamlessUpgradeToProModal' */ '../SeamlessUpgradeToProModal');
            break;
        case null:
            break;
        default:
            deprecatedLogger({
                componentName: 'MigrationModalManager',
                message: `Unknown modal type: ${modalType}`,
                source: modalPayload.source,
                biEnrichment: modalPayload.biEnrichment,
            });
            break;
    }

    if (lazyImport) {
        return (
            <LazyComponent lazyImport={lazyImport} shouldImport componentProps={seamlessModalProps} onError={onError} />
        );
    }

    return null;
};

export default MigrationModalManager;
